import { useEffect, useRef, useState } from 'react';
import { Check, ExternalLink, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { matchedLinkFields, updateRequest, type SongRequest } from '../../services/requests';
import {
  librarySongOf,
  reportWrongSong,
  resolveSpotifyLink,
  trySaveSongLink,
} from '../../services/songMatchMemory';
import { searchUrlFor, type PlayModeConfig } from '../../services/playModes';
import { cleanPastedLink } from '../../utils/songLinks';

export interface WrongSongPanelProps {
  request: SongRequest;
  playMode: PlayModeConfig;
  mode: 'report' | 'fix';
  onDone: () => void;
}

const FIX_QUEUED_MESSAGE = 'Link fixed for this song. Your library edits are paused, so it was sent to the owner.';

function openSearch(url: string) {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener');
  } else {
    window.location.href = url;
  }
}

function errorText(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'unknown error';
}

export function WrongSongPanel({ request, playMode, mode, onDone }: WrongSongPanelProps) {
  const [pastedUrl, setPastedUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const reportedRef = useRef(false);

  const searchAgain = () => openSearch(searchUrlFor(request.song.title, request.song.artist, playMode));

  useEffect(() => {
    if (mode !== 'report' || reportedRef.current) return;
    reportedRef.current = true;
    openSearch(searchUrlFor(request.song.title, request.song.artist, playMode));
    reportWrongSong(request.id)
      .then(({ kind }) => {
        if (kind === 'library') toast.success("Reported. This link's count was reset.");
        else if (kind === 'glitch') toast.success('Reported to the owner.');
      })
      .catch((err: unknown) => {
        console.error('[WrongSongPanel] Reporting the wrong song failed:', err);
        toast.error(`Couldn't log the report: ${errorText(err)}`);
      });
  }, [mode, request.id, request.song.title, request.song.artist, playMode]);

  const handleUseLink = async () => {
    if (!pastedUrl.trim()) return;
    setSaving(true);
    try {
      const cleaned = await cleanPastedLink(pastedUrl, resolveSpotifyLink);
      if (!cleaned.ok) {
        toast.error(cleaned.message);
        return;
      }
      if (cleaned.provider !== playMode.provider) {
        toast.error(`That's a ${cleaned.platformName} link. Paste a ${playMode.playerName} link here.`);
        return;
      }

      await updateRequest(request.id, {
        ...matchedLinkFields({ platformLink: cleaned.url, platformName: cleaned.platformName }, request.song),
        songLinkUrl: undefined,
        wrongSongFixedAt: new Date().toISOString(),
      });

      const libraryEntry = request.linkLibraryKey
        ? { songKey: request.linkLibraryKey, link: cleaned.url, eventId: request.eventId, requestId: request.id }
        : { song: librarySongOf(request.song), link: cleaned.url, eventId: request.eventId, requestId: request.id };
      trySaveSongLink(libraryEntry).then(outcome => {
        if (outcome.error) toast.error(`Link fixed for this song, but the library save failed: ${outcome.error}`);
        else if (outcome.queuedForReview) toast(FIX_QUEUED_MESSAGE, { duration: 6000 });
        else toast.success('Link fixed and saved.');
      });
      onDone();
    } catch (err) {
      console.error('[WrongSongPanel] Fixing the link failed:', err);
      toast.error(`Couldn't fix the link: ${errorText(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
          {mode === 'report'
            ? `Wrong song? Find the right one in ${playMode.playerName}, then paste its link here.`
            : `Paste the right ${playMode.playerName} link for this song.`}
        </p>
        <button
          onClick={onDone}
          className="p-1 rounded text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800/40"
          title="Close"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={searchAgain}
          className="px-2 py-1.5 text-xs rounded bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800/40 flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" /> Search {playMode.playerName} again
        </button>
        <input
          type="text"
          value={pastedUrl}
          onChange={e => setPastedUrl(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleUseLink(); }}
          className="input text-xs flex-1 min-w-[160px]"
          placeholder={`Paste a ${playMode.playerName} track link`}
        />
        <button
          onClick={handleUseLink}
          disabled={saving || !pastedUrl.trim()}
          className="px-2 py-1.5 text-xs bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Use this link
        </button>
      </div>
    </div>
  );
}
